import { ApiError, canonicalJSON, parseView, type Action } from './transport';
const transition = new Set(['workspace.importCommit', 'workspace.importRollback']), fields = ['title', 'description', 'price', 'currency', 'sku', 'quantity', 'tags'];
const exact = (v: unknown, keys: string[]): v is Record<string, any> => !!v && typeof v === 'object' && !Array.isArray(v) && Object.keys(v).sort().join() === keys.slice().sort().join(), same = (a: unknown, b: unknown) => canonicalJSON(a) === canonicalJSON(b), positive = (n: unknown) => Number.isSafeInteger(n) && Number(n) > 0;
function invalid(): never { throw new ApiError('denied', 'INVALID_WORKSPACE_PREPARATION'); }
async function digest(text: string) { return Array.from(new Uint8Array(await crypto.subtle.digest('SHA-256', new TextEncoder().encode(text))), b => b.toString(16).padStart(2, '0')).join(''); }
/** Closed S03 source shapes only; original R03 manifest export stays separate. */
export function isWorkspaceBulkAction(a: Action) { return a.id === 'workspace.importPreview' && exact(a.input, ['upload', 'columns', 'locale']) || transition.has(a.id) && exact(a.input, ['preview', 'rollbackToken']) || a.id === 'workspace.export' && exact(a.input, ['preview']); }
export async function parsePreparedWorkspaceBulk(a: Action, input: Record<string, unknown>, response: unknown): Promise<Action> {
    const r = response as any;
    if (!isWorkspaceBulkAction(a) || r?.kind !== 'ok' || !positive(r.version))
        invalid();
    const raw = r.value?.preparedAction;
    parseView({ id: 'workspace-preparation', version: r.version, items: [], actions: [raw] });
    const p = { ...raw, confirmation: raw.immutableconfirmation } as Action, c = p.confirmation;
    if (!a.protected || !p.protected || p.id !== a.id || p.instanceId === a.instanceId || p.expectedVersion !== a.expectedVersion || !Array.isArray(p.fields) || p.fields.length || !p.resource || !p.expiresAt || Date.parse(p.expiresAt) <= Date.now() || !c?.principal || !same(c.resource, p.resource) || !c.before || !c.after || c.amount || c.preparedByAgent || !/^[a-f0-9]{64}$/.test(c.digest ?? ''))
        invalid();
    if (a.id === 'workspace.importPreview') {
        const i = p.input;
        if (!exact(input, ['upload', 'columns', 'locale']) || !exact(input.upload, ['id', 'version']) || !positive(input.upload.version) || input.locale !== a.input.locale || !exact(input.columns, fields) || !exact(i, ['shopId', 'upload', 'locale', 'fieldMapping', 'csv', 'uploadedAt', 'dryRun']) || !exact(i.upload, ['id', 'digest', 'access']) || i.upload.id !== input.upload.id || i.upload.access !== 'restricted' || p.resource.id !== input.upload.id || p.resource.version !== input.upload.version || p.subject !== a.subject || a.expectedVersion !== null || typeof i.shopId !== 'string' || !i.shopId || i.locale !== input.locale || i.dryRun !== true || typeof i.csv !== 'string' || new TextEncoder().encode(i.csv).length > 2 * 1024 * 1024 || typeof i.uploadedAt !== 'string' || !Number.isFinite(Date.parse(i.uploadedAt)) || Date.parse(i.uploadedAt) > Date.now())
            invalid();
        const entries = fields.map(field => { const column = (input.columns as Record<string, unknown>)[field]; if (typeof column !== 'string' || column.length > 256)
            invalid(); return [column, field] as const; }).filter(([column]) => column !== '');
        if (new Set(entries.map(([column]) => column)).size !== entries.length)
            invalid();
        const mapping = Object.fromEntries(entries);
        if (!same(i.fieldMapping, mapping) || !/^[a-f0-9]{64}$/.test(String(i.upload.digest)) || await digest(i.csv) !== i.upload.digest)
            invalid();
    }
    else if (!same(a.resource, p.resource) || a.subject !== p.subject || !same(input, a.input) || !same(p.input, input))
        invalid();
    if (c.digest !== await digest(canonicalJSON({ operation: p.id, expectedVersion: p.expectedVersion, input: p.input })))
        invalid();
    return p;
}
/** Exact ORIGINAL staging export DTO, full digest and bounded quoted CSV only.
 * A failed download parse never proves a command had no effect. The original
 * Pending/unknown key remains owned by its ordinary executor/status protocol. */
export async function workspaceStagingAttachment(action: Action, value: unknown): Promise<{
    blob: Blob;
    filename: string;
}> {
    if (action.id !== 'workspace.export' || !exact(action.input, ['preview']) || !exact(action.input.preview, ['id', 'version']) || !positive(action.input.preview.version) || !exact(value, ['id', 'locale', 'format', 'csv', 'digest', 'scope']) || value.scope !== 'PRIVATE_IMPORT_STAGING_DRAFTS' || value.id !== action.input.preview.id || value.format !== 'csv' || !['en', 'de', 'fr', 'es', 'ja', 'ar'].includes(value.locale) || typeof value.csv !== 'string' || new TextEncoder().encode(value.csv).length > 8 * 1024 * 1024 || !/^[a-f0-9]{64}$/.test(value.digest) || await digest(value.csv) !== value.digest)
        invalid();
    const csv = value.csv;
    let i = 0, count = 0;
    while (i < csv.length) {
        const cells: string[] = [];
        while (true) {
            if (csv[i++] !== '"')
                invalid();
            let cell = '', closed = false;
            while (i < csv.length) {
                const ch = csv[i++];
                if (ch === '"') {
                    if (csv[i] === '"') {
                        cell += '"';
                        i++;
                        continue;
                    }
                    closed = true;
                    break;
                }
                cell += ch;
                if (cell.length > 4000)
                    invalid();
            }
            if (!closed || /[\u0000-\u0008\u000b\u000c\u000e-\u001f\u007f]/.test(cell))
                invalid();
            const trim = cell.replace(/^[ \t]+/, '');
            if (/^[=+@]/.test(trim) || trim.startsWith('-') && !/^-\d+(?:[.,]\d+)?$/.test(trim))
                invalid();
            cells.push(cell);
            if (cells.length > 7)
                invalid();
            if (csv[i] === ',') {
                i++;
                continue;
            }
            if (csv.slice(i, i + 2) !== '\r\n')
                invalid();
            i += 2;
            break;
        }
        if (cells.length !== 7 || count === 0 && !same(cells, fields) || ++count > 5001)
            invalid();
    }
    if (count === 0)
        invalid();
    return { blob: new Blob([csv], { type: 'text/csv;charset=utf-8' }), filename: 'fablelot-private-import.csv' };
}
